import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { Header, VStack } from 'tio-ui/components/layout';
import {
  PhPath,
  PhEyeSlash,
  PhEye,
  PhMoon,
  PhSun,
  PhSparkle,
  PhWarning,
  PhChatCircleDots,
} from 'ember-phosphor-icons';
import MegaMenuNav from 'tio-employee/components/reimagined/mega-menu-nav';
import DataSourceDocs from 'tio-employee/components/reimagined/data-source-docs';
import ReviewDrawer from 'tio-employee/components/reimagined/review-drawer';
import ToastContainer from 'tio-ui/components/toast';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type ComponentReviewService from 'tio-employee/services/component-review';
import type ReimaginedToastService from 'tio-employee/services/reimagined-toast';
import type DemoModeService from 'tio-employee/services/demo-mode';
import { getDemoScenarioIdFromUrl } from 'tio-employee/utils/career-pathways-demo-data';

export default class ReimagainedLayout extends Component {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare componentReview: ComponentReviewService;
  @service declare demoMode: DemoModeService;
  @service('reimagined-toast') declare toast: ReimaginedToastService;

  @tracked isLegacyFrameHidden = this.initLegacyFrameState();
  private darkOnEntry = this.initDarkState();
  @tracked isDarkMode = this.darkOnEntry;

  constructor(owner: Owner, args: object) {
    super(owner, args);
    // Apply legacy frame state on init
    if (this.isLegacyFrameHidden) {
      this.applyLegacyFrameState(true);
    }
  }

  /**
   * `.dark` lives on <html>, which outlives this route, so put it back the way
   * we found it on teardown — otherwise the demo toggle leaks dark tokens into
   * the legacy employee pages (tio-theme.css selects on
   * `[data-mode="dark"], .dark`). Restores rather than clears so a `.dark` set
   * by anything else survives.
   */
  willDestroy() {
    if (typeof window !== 'undefined') {
      document.documentElement.classList.toggle('dark', this.darkOnEntry);
    }
    super.willDestroy();
  }

  private initLegacyFrameState() {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('reimagined-legacy-frame-hidden') === 'true';
  }

  private initDarkState() {
    if (typeof window === 'undefined') {
      return false;
    }
    return document.documentElement.classList.contains('dark');
  }

  private applyLegacyFrameState(hidden: boolean) {
    const legacyBanner = document.querySelector('[data-legacy-banner]');
    const legacySidebar = document.querySelector('[data-legacy-sidebar]');
    if (legacyBanner) {
      legacyBanner.classList.toggle('hidden', hidden);
    }
    if (legacySidebar) {
      legacySidebar.classList.toggle('hidden', hidden);
    }
    document.body.classList.toggle('reimagined-fullscreen', hidden);
  }

  /**
   * Base scenario id for the mega-menu nav's hideFor filtering. Read through
   * the NORMALIZED source (products win over a conflicting explicit
   * demo_scenario), so the nav can never disagree with page content the way
   * the retired header strip's independent scenario write allowed. Computed
   * once per layout instantiation — every session change goes through a full
   * reload (applyDemoSession), matching the old tracked-prop behavior.
   */
  get navScenarioId() {
    return getDemoScenarioIdFromUrl();
  }

  /**
   * Launcher for THE demo control surface — the global picker modal
   * (DemoDataShortcut). Replaces the retired header strip's three dropdowns
   * and on/off toggle: entry, adjustments, and exit all live in one place
   * (the picker + the demo pill).
   */
  openDemoPicker = () => {
    this.demoMode.openPicker();
  };

  toggleLegacyFrame = () => {
    this.isLegacyFrameHidden = !this.isLegacyFrameHidden;
    this.applyLegacyFrameState(this.isLegacyFrameHidden);
    // Persist to localStorage for page refresh persistence
    localStorage.setItem('reimagined-legacy-frame-hidden', String(this.isLegacyFrameHidden));
  };

  toggleDarkMode = () => {
    this.isDarkMode = !this.isDarkMode;
    // Toggle dark class on html element for CSS variable inheritance
    document.documentElement.classList.toggle('dark', this.isDarkMode);
  };

  // Data audit state is managed by reimaginedData service for reactivity across components

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="min-h-screen overflow-hidden tio-bg-surface isolate">
      <Header class="relative z-30 border-b py-4 shrink-0 tio-border-default">
        <div class="w-full {{if this.isLegacyFrameHidden 'px-4' 'max-w-7xl mx-auto px-4'}}">
          <div class="tio-row-between gap-4">
            <div class="tio-row">
              <PhPath @size={{32}} class="tio-fg-brand" />
              <span class="tio-text-2xl-bold tio-fg-primary">TIO Reimagined</span>
            </div>

            <div class="tio-row">
              {{! Demo Control Buttons }}
              <div class="tio-row-tight pl-3 border-l tio-border-strong">
                {{! Hide Legacy Frame Toggle }}
                <button
                  type="button"
                  class="tio-demo-toggle
                    {{if
                      this.isLegacyFrameHidden
                      'tio-demo-toggle--active tio-demo-toggle--warning'
                    }}"
                  title="{{if this.isLegacyFrameHidden 'Show legacy frame' 'Hide legacy frame'}}"
                  {{on "click" this.toggleLegacyFrame}}
                >
                  {{#if this.isLegacyFrameHidden}}
                    <PhEye @size={{20}} />
                  {{else}}
                    <PhEyeSlash @size={{20}} />
                  {{/if}}
                </button>

                {{! Demo settings — opens THE demo control surface (the global
                    picker modal). The strip's per-axis dropdowns and on/off
                    toggle are retired: enter/adjust via the picker's Apply,
                    exit via the picker footer or the floating demo pill. }}
                {{#if this.demoMode.isAvailable}}
                  <button
                    type="button"
                    class="tio-demo-toggle
                      {{if
                        this.demoMode.isActive
                        'tio-demo-toggle--active tio-demo-toggle--brand'
                      }}"
                    title="Demo settings (Ctrl+Shift+~)"
                    aria-label="Demo settings (Ctrl+Shift+~)"
                    aria-haspopup="dialog"
                    data-test-demo-settings-launcher
                    {{on "click" this.openDemoPicker}}
                  >
                    <PhSparkle @size={{20}} />
                  </button>
                {{/if}}

                {{! Data Audit Toggle (rose at the legacy call site → danger) }}
                <button
                  type="button"
                  class="tio-demo-toggle
                    {{if
                      this.reimaginedData.isDataAuditEnabled
                      'tio-demo-toggle--active tio-demo-toggle--danger'
                    }}"
                  title="{{if
                    this.reimaginedData.isDataAuditEnabled
                    'Disable data audit'
                    'Enable data audit'
                  }}"
                  {{on "click" this.reimaginedData.toggleDataAudit}}
                >
                  <PhWarning @size={{20}} />
                </button>

                {{! Component Review Toggle }}
                <button
                  type="button"
                  class="tio-demo-toggle
                    {{if
                      this.componentReview.isReviewModeEnabled
                      'tio-demo-toggle--active tio-demo-toggle--brand'
                    }}"
                  title="{{if
                    this.componentReview.isReviewModeEnabled
                    'Disable component review'
                    'Enable component review'
                  }}"
                  {{on "click" this.componentReview.toggleReviewMode}}
                >
                  <PhChatCircleDots @size={{20}} />
                </button>

                {{! Dark Mode Toggle (indigo at the legacy call site → brand) }}
                <button
                  type="button"
                  data-test-dark-mode-toggle
                  class="tio-demo-toggle
                    {{if this.isDarkMode 'tio-demo-toggle--active tio-demo-toggle--brand'}}"
                  title="{{if this.isDarkMode 'Switch to light mode' 'Switch to dark mode'}}"
                  {{on "click" this.toggleDarkMode}}
                >
                  {{#if this.isDarkMode}}
                    <PhSun @size={{20}} />
                  {{else}}
                    <PhMoon @size={{20}} />
                  {{/if}}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Header>

      {{! Reimagined Navigation - Mega-menu with dropdowns }}
      <MegaMenuNav @scenarioId={{this.navScenarioId}} @isFullscreen={{this.isLegacyFrameHidden}} />

      {{! Main content area - constrained width with proper overflow }}
      <div class="tio-fill-min overflow-y-auto overflow-x-hidden tio-bg-surface">
        <div
          class="w-full pt-2 pb-6 {{if this.isLegacyFrameHidden 'px-4' 'max-w-7xl mx-auto px-4'}}"
        >
          {{outlet}}
        </div>
      </div>

      {{! Data Source Documentation Panel - dev only }}
      <DataSourceDocs
        @isOpen={{this.reimaginedData.isDocsOpen}}
        @onClose={{this.reimaginedData.closeDocs}}
        @sectionId={{this.reimaginedData.docsSectionId}}
      />

      {{! Component Review Thread Panel }}
      <ReviewDrawer
        @isOpen={{this.componentReview.isDrawerOpen}}
        @onClose={{this.componentReview.closeDrawer}}
        @reviewId={{this.componentReview.activeReviewId}}
        @pagePath={{this.componentReview.activePagePath}}
      />

      {{! Reimagined Toast Notifications }}
      <ToastContainer @toasts={{this.toast.toasts}} @onDismiss={{this.toast.dismiss}} />
    </VStack>
  </template>
}
